import { cached } from '@glimmer/tracking';

import type { DateRangeInterface, DSData } from 'webapp-types';
import { OrderDirection, SortFieldType } from 'webapp/enums/enums';
import { formatNumber } from 'webapp/utils/number';

import type { MetricSelectionData } from 'webapp/models-custom/ds-data/output/top';
import type DChartComparePoint from 'webapp/d-chart/point/compare-point';
import type { PointBaseData } from 'webapp/d-chart/point/point-base';
import DChartPointBase from 'webapp/d-chart/point/point-base';
import type DChartSeries from 'webapp/d-chart/series/series';
import { FALLBACK_COLOR_INDEX } from 'webapp/d-chart/series/series';
import DChartTooltipBase from 'webapp/d-chart/tooltip/base';
import DChartTooltipForecast from 'webapp/d-chart/tooltip/forecast';

export type PointData = PointBaseData & {
	index?: number;
	value?: number | null;
	valueStr?: string;
	granulationLabel?: DSData.GranulationLabel;
	incomplete?: boolean;
	loading?: boolean;
	mediaLoading?: boolean; // used for media creatives images loading status
	titleLong?: string; // used for full metric name (tooltip top)
	format?: string;
	scale?: number | null;
	metricSelectionData?: MetricSelectionData;
	dateRange?: DateRangeInterface.FormulaInterface | null;
	isSourceDataset?: boolean;
};

export default class DChartPrimaryPoint extends DChartPointBase {
	// number if from theme, string (custom-color-x) if user-custom
	// this will be reflected in the classes (e.g. colors in point tooltip)
	colorIndexOrClass: string | number = FALLBACK_COLOR_INDEX;

	index: number;
	incomplete: boolean;
	loading: boolean;
	mediaLoading: boolean;

	titleLong: string;
	format: string;
	scale: number | null = null;
	metricSelectionData: MetricSelectionData | null;
	dateRange: DateRangeInterface.FormulaInterface | null = null; // dateRange formula from dataSelect

	series: DChartSeries | null = null; // optionally set when creating Point

	comparePoints: DChartComparePoint[] = [];

	tooltip: DChartTooltipBase;

	isSourceDataset: boolean;

	constructor(pointData: PointData = {}) {
		super(pointData);

		if (typeof pointData.scale === 'number') {
			this.scale = pointData.scale;
		}

		this.index = pointData.index || 0;
		this.incomplete = !!pointData.incomplete;
		this.loading = !!pointData.loading;
		this.mediaLoading = !!pointData.mediaLoading;
		this.titleLong = pointData.titleLong || this.title;
		this.format = pointData.format || '';
		this.metricSelectionData = pointData.metricSelectionData || null;
		this.isSourceDataset = pointData.isSourceDataset || false;

		if (pointData.dateRange) {
			this.dateRange = pointData.dateRange;
		}

		if (!pointData.valueStr && this.hasValue) {
			this.valueStr = formatNumber(this.value!, this.format, { scale: this.scale });
		}

		this.tooltip = this.createTooltip();
	}

	get hasValue() {
		return typeof this.value === 'number';
	}

	get hasCompare() {
		return this.comparePoints.length > 0;
	}

	get timestamp(): number {
		return this.granulationLabel.ts;
	}

	get timestampEnd(): number {
		return this.granulationLabel.endTs;
	}

	get timeLabel(): string {
		return this.granulationLabel.label;
	}

	get timeLabelLong(): string {
		return this.granulationLabel.labelLong;
	}

	get hasNoColorIndexOrClass() {
		return this.colorIndexOrClass === FALLBACK_COLOR_INDEX;
	}

	get dsId() {
		return this.metricSelectionData?.dsId;
	}

	get isRelativeTimeFormat() {
		return this.format === 'R';
	}

	get valueStrFormatted() {
		// same as valueStr, but accomodates relative time format that's always formatted on FE
		// the component including this must add timerService.ticket to autotracking
		if (this.hasValue && this.isRelativeTimeFormat) {
			return formatNumber(this.value!, this.format, {
				isHtml: true,
				scale: this.scale,
			});
		} else {
			return this.valueStr;
		}
	}

	@cached
	get valueStrWithHtml() {
		return formatNumber(this.value!, this.format, { isHtml: true, scale: this.scale });
	}

	@cached
	get tooltipHtml() {
		return this.getTooltipHtmlForPoint();
	}

	get tooltipTypeClass() {
		return this.tooltip.tooltipTypeClass;
	}

	get isActionable() {
		return this.onClick;
	}

	getTooltipHtmlForPoint(comparePoint?: DChartComparePoint) {
		return this.tooltip.getTooltipHtmlForPoint(comparePoint);
	}

	recreateTooltip() {
		const tooltip = this.createTooltip();
		this.tooltip = tooltip;
		return tooltip;
	}

	private createTooltip() {
		// If any compare is a forecast, we use the Forecast type tooltip layout
		// This will need to be changed if/when we want to show Forecasts in normal blocks etc.
		const isForecastTooltip = this.comparePoints.some((p) => p.isOneOfForecasts);
		const tooltip = isForecastTooltip
			? new DChartTooltipForecast(this)
			: new DChartTooltipBase(this);
		return tooltip;
	}

	static doPointCompare(
		point1: DChartPrimaryPoint | null | undefined,
		point2: DChartPrimaryPoint | null | undefined,
		sortField: SortFieldType | null,
		sortOrder: OrderDirection,
	): number {
		let mustApplyReverse = sortOrder === OrderDirection.DESC;

		if (!point1 && !point2) {
			return 0; // keep given order
		} else if (!point1) {
			return 1; // point2 before point1
		} else if (!point2) {
			return -1; // point1 before point2
		} else {
			let result = 0;
			switch (sortField) {
				case SortFieldType.CUSTOM:
					return 0; // dont apply sortorder
				case SortFieldType.NAME:
					result = point1.title.localeCompare(point2.title);
					break;
				case SortFieldType.CHANGE: {
					const comparePoint1 = point1.comparePoints[0],
						comparePoint2 = point2.comparePoints[0];
					if (!comparePoint1 && !comparePoint2) {
						result = 0;
					} else if (!comparePoint1) {
						result = 1;
					} else if (!comparePoint2) {
						result = -1;
					} else {
						result = comparePoint1.change - comparePoint2.change;
					}

					// if any of the values is null (missing), it must be always on bottom
					// doValueCompare already does so, which means we must skip applying DESC sort
					if (!comparePoint1 || !comparePoint1.hasValue || !comparePoint2?.hasValue) {
						mustApplyReverse = false;
					}

					break;
				}
				case SortFieldType.VALUE:
				default: {
					result = this.doValueCompare(point1.value, point2.value);
					// if any of the values is null (missing), it must be always on bottom
					// doValueCompare already does so, which means we must skip applying DESC sort
					if (!point1.hasValue || !point2.hasValue) {
						mustApplyReverse = false;
					}
					break;
				}
			}

			if (mustApplyReverse) {
				// reverse the result
				if (result > 0) {
					result = -1;
				} else if (result < 0) {
					result = 1;
				}
			}

			return result;
		}
	}

	static doValueCompare(value1: number | null, value2: number | null): number {
		let result;
		if (value1 === null && value2 === null) {
			result = 0;
		} else if (value1 === null) {
			result = 1;
		} else if (value2 === null) {
			result = -1;
		} else {
			result = value1 - value2;
		}
		return result;
	}
}
